const { Form } = require('antd');
const { message } = require('antd');
const { useState, useEffect, useCallback } = require('react');
const { consultationService } = require('../../../services/medicalConsultation');

const useRecommendationsModal = ({
  t,
  service,
  patientFullName,
  countryCode,
  conclusionPosition,
  setConclusionText,
  oldReport,
  oldReportCountry,
  medicalForm,
  countrySettings,
}) => {
  const countryIndication = t('medicalConsultations.healthForm.indications');
  const countrySuggestion = t('medicalConsultations.healthForm.suggestions');
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState();
  const [modalVisible, setModalVisible] = useState({
    state: false,
    type: null,
    from: null,
    diagnosticKey: null,
    diagnosticField: null,
  });
  const [filteredConclusions, setFilteredConclusions] = useState([]);
  const [selectedConclusions, setSelectedConclusions] = useState([]);
  const [diagnostic, setDiagnostic] = useState();
  const [form] = Form.useForm();
  const [data, setData] = useState({
    service: null,
    form: null,
    patientFullName: null,
    countryCode: null,
  });

  useEffect(() => {
    if (service && patientFullName && countryCode && !data.service) {
      setData({
        service,
        patientFullName,
        countryCode,
      });
    }
  }, [countryCode, patientFullName, service]);


  const showModal = ({ type, from, diagnosticKey, diagnosticField = 'diagnostics' }) => {
    setModalVisible({ state: true, type, from, diagnosticKey, diagnosticField });

    if (from === 'diagnostics' && countrySettings.medicalConsultation.showDiagnostics[countryCode] && medicalForm) {
      let diagnostics;

      if (Array.isArray(diagnosticField)) {
        const [parentField, index, childField] = diagnosticField;
        const parentArray = medicalForm.getFieldValue(parentField) || [];
        diagnostics = parentArray[index]?.[childField] || [];
      } else {
        diagnostics = medicalForm.getFieldValue(diagnosticField) || [];
      }

      setDiagnostic(diagnostics[diagnosticKey]);
    }
    fetchList(type);
  };

  const searchConclusion = (event) => {
    let inputValue = event.target.value;

    if (inputValue) {
      inputValue = inputValue.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

      const filterList = list.filter((item) => {
        return (
          item.code
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .includes(inputValue)
        );
      });

      setFilteredConclusions([...filterList]);
    } else {
      setFilteredConclusions([...list]);
    }
  };

  const fetchList = async (type) => {
    const department = service.medical_form_type === 'health' ? 1 : 2;
    try {
      setLoading(true);
      let response;
      if (type === 'indication') {
        response = await consultationService.getIndications({
          serviceId: service.id,
          serviceType: service.type,
          medicalFormType: service.medicalFormType,
          department: department
        });
      } else {
        response = await consultationService.getSuggestions({
          serviceId: service.id,
          serviceType: service.type,
          medicalFormType: service.medicalFormType,
          department: department
        });
      }
      setList(response);
      setFilteredConclusions(response);
    } catch (error) {
      console.error(error);
      message.error({
        content: `Ha ocurrido un error al cargar las ${modalVisible.type === "indication" ? countryIndication : countrySuggestion
          }`,
        style: {
          marginTop: "35vh",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const closeModal = useCallback(() => {
    form.resetFields();
    setSelectedConclusions([]);
    setFilteredConclusions([...list]);
    setModalVisible({ state: false, type: null, from: null });
  }, [form, list]);

  const addConclusions = useCallback(() => {
    if (!selectedConclusions?.length) {
      message.error({
        content: `No se han seleccionado ${modalVisible.type === "indication" ? countryIndication : countrySuggestion
          } para agregar`,
        style: {
          marginTop: "35vh",
        },
      });
      return;
    }

    if (!countrySettings.medicalConsultation.showIndicationsAndSuggestions[countryCode] && (oldReport && oldReportCountry)) {
      const conclusionText = medicalForm.getFieldValue('conclusion');
      const countryWord = (modalVisible.type === 'indication' ? countryIndication : countrySuggestion).toUpperCase();

      const addNewText = conclusionText.substr(0, conclusionPosition)
        .concat(
          conclusionText === '' ? '' : `${conclusionText.includes(countryWord) ? '' : `${countryWord}\n`}`,
          selectedConclusions.join('\n'),
          conclusionText.substr(conclusionPosition),
          '\n'
        );

      setConclusionText(addNewText);
      medicalForm.setFieldsValue({ conclusion: addNewText });
      closeModal();
    } else if (modalVisible.from === 'conclusions') {
      const existingText = medicalForm.getFieldValue(modalVisible.type) || "";
      let updatedText;

      if (countrySettings.medicalConsultation.defaultIndicationBackText[countryCode] && modalVisible.type === 'indication') {
        updatedText = `${selectedConclusions.join("\n")}\n${existingText}`;
      } else {
        updatedText = existingText.length > 0
          ? `${existingText}\n${selectedConclusions.join("\n")}`
          : selectedConclusions.join("\n");
      }

      medicalForm.setFieldsValue({ [modalVisible.type]: updatedText });
      closeModal();
      return;
    }

    const diagnosticFieldPath = Array.isArray(modalVisible.diagnosticField)
      ? modalVisible.diagnosticField
      : [modalVisible.diagnosticField];
    const topLevelValue = [...(medicalForm.getFieldValue(diagnosticFieldPath[0]) || [])];
    let parent = topLevelValue;
    parent = diagnosticFieldPath.slice(1).reduce((currentParent, pathSegment) => {
      return currentParent[pathSegment];
    }, parent);


    const fieldKey = modalVisible.diagnosticKey;
    const oldText = parent[fieldKey]?.[modalVisible.type] || "";
    const newText = oldText.length > 0
      ? `${oldText}\n${selectedConclusions.join("\n")}`
      : selectedConclusions.join("\n");

    parent[fieldKey] = {
      ...parent[fieldKey],
      [modalVisible.type]: newText,
    };
    medicalForm.setFieldsValue({ [diagnosticFieldPath[0]]: topLevelValue });

    closeModal();
  }, [selectedConclusions, modalVisible, closeModal, medicalForm]);


  const onCheck = (e) => {
    const value = e.target.value;
    const findedSelected = filteredConclusions.find((conclusion) => conclusion.id === value);
    const index = selectedConclusions.indexOf(findedSelected.description);

    if (index === -1) {
      setSelectedConclusions([...selectedConclusions, findedSelected.description]);
    } else {
      setSelectedConclusions(selectedConclusions.filter((item) => item !== findedSelected.description));
    }
  }

  return {
    modalVisible,
    setModalVisible,
    closeModal,
    showModal,
    ...data,
    form,
    addConclusions,
    countryIndication,
    countrySuggestion,
    list,
    loading,
    filteredConclusions,
    setFilteredConclusions,
    selectedConclusions,
    onCheck,
    searchConclusion,
  };
}

export default useRecommendationsModal;
